class NotificationService {
  constructor() {
    this.notifications = this.loadFromStorage();
    // Ensure notifications is always an array
    if (!Array.isArray(this.notifications)) {
      this.notifications = [];
    }
    this.listeners = [];
    this.nextId = this.getNextId();
  }

  // Load notifications from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('notifications');
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
      return [];
    }
  }

  // Save notifications to localStorage
  saveToStorage() {
    try {
      localStorage.setItem('notifications', JSON.stringify(this.notifications));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }

  // Get next available ID
  getNextId() {
    const list = Array.isArray(this.notifications) ? this.notifications : [];
    const maxId = list.reduce((max, notification) => {
      const idNum = Number(notification && notification.id);
      return Math.max(max, Number.isFinite(idNum) ? idNum : 0);
    }, 0);
    return maxId + 1;
  }

  // Add a listener for notification updates
  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  // Notify all listeners of notification updates
  notify() {
    this.listeners.forEach(listener => listener(this.notifications));
  }

  // Add a new notification from toast data
  addFromToast(toast) {
    const notification = {
      id: this.nextId++,
      message: toast.message,
      type: toast.type,
      timestamp: toast.timestamp,
      isRead: false,
      toastId: toast.id
    };

    this.notifications.unshift(notification); // Add to beginning
    this.saveToStorage();
    this.notify();
    return notification.id;
  }

  // Mark notification as read
  markAsRead(id) {
    const notification = this.notifications.find(n => n.id === id);
    if (notification && !notification.isRead) {
      notification.isRead = true;
      this.saveToStorage();
      this.notify();
    }
  }

  // Mark all notifications as read
  markAllAsRead() {
    let hasChanges = false;
    this.notifications.forEach(notification => {
      if (!notification.isRead) {
        notification.isRead = true;
        hasChanges = true;
      }
    });
    
    if (hasChanges) {
      this.saveToStorage();
      this.notify();
    }
  }

  // Get unread count
  getUnreadCount() {
    return this.notifications.filter(n => !n.isRead).length;
  }

  // Get all notifications
  getAll() {
    return this.notifications;
  }

  // Get recent notifications (last 10)
  getRecent(limit = 10) {
    return this.notifications.slice(0, limit);
  }

  // Remove old notifications (keep last 50)
  cleanup(maxNotifications = 50) {
    if (this.notifications.length > maxNotifications) {
      this.notifications = this.notifications.slice(0, maxNotifications);
      this.saveToStorage();
      this.notify();
    }
  }

  // Clear all notifications
  clear() {
    this.notifications = [];
    this.saveToStorage();
    this.notify();
  }

  // Get icon class for notification type
  getIconClass(type) {
    switch (type) {
      case 'success':
        return 'bi bi-check-circle-fill text-success';
      case 'info':
        return 'bi bi-info-circle-fill text-info';
      case 'warning':
        return 'bi bi-exclamation-triangle-fill text-warning';
      case 'danger':
      case 'error':
        return 'bi bi-x-circle-fill text-danger';
      default:
        return 'bi bi-bell-fill text-primary';
    }
  }

  // Format timestamp for display
  formatTime(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return new Date(timestamp).toLocaleDateString();
  }
}

// Create and export a singleton instance
const notificationService = new NotificationService();
export default notificationService;